// Copyright (C) 2021 Bystronic Laser AG

import { computed, ref } from 'vue';

/**
 * A composable function to handle long press (or long tap) interactions on mobile and tablet devices.
 * It provides `mouseDown` and `mouseUp` event handlers to detect when a user presses and releases
 * a touch or click interaction. If the interaction lasts longer than the specified delay, it triggers
 * a callback function (`onLongPress`).
 *
 * @param onLongPress - A callback function that is triggered when a long press is detected.
 *                      It receives the event object as an argument.
 * @param delay - The duration (in milliseconds) that the press must last to be considered a long press.
 *                Defaults to 500ms.
 * @returns An object containing the `mouseDown` and `mouseUp` event handlers to be used in the template.
 *
 * @example
 * ```vue
 * <template>
 *   <div @mousedown="mouseDown" @mouseup="mouseUp">
 *     Press and hold me!
 *   </div>
 * </template>
 *
 * <script setup lang="ts">
 * import { useOnLongTap } from '@/composables/useOnLongTap';
 *
 * const { mouseDown, mouseUp } = useOnLongTap((event) => {
 *   console.log('Long press detected!', event);
 *   onExpandOnDblClick(event);
 * });
 * </script>
 * ```
 */
export function useOnLongTap(onLongPress: (event: any) => void, delay = 500) {
  const longPressStart = ref(0);
  const longPressEnd = ref(0);

  function mouseDown(event: any) {
    if (isMobileOrTablet.value) {
      longPressStart.value = Date.now();
    }
  }

  function mouseUp(event: any) {
    if (isMobileOrTablet.value) {
      longPressEnd.value = Date.now();
      if (longPressEnd.value - longPressStart.value > delay) {
        onLongPress(event);
      }
    }
  }

  const isMobileOrTablet = computed(() => {
    const ua = navigator.userAgent;
    return (
      /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua) ||
      /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        ua,
      ) ||
      // iPad on iOS 13 and up detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
  });

  return {
    mouseDown,
    mouseUp,
  };
}
